// Pure scheduling/eligibility logic for the presence heartbeat (FCA-6979).
//
// Kept free of React and of module-level state on purpose: every rule that
// decides whether a heartbeat is sent is expressed here as a plain function, so
// the expensive-to-get-wrong behaviour (suppressed on hidden tabs, silent when
// the flag is off, one beat per browser rather than per tab) is unit-testable
// without rendering anything or faking timers around a hook.
//
// Cost context: every heartbeat is an authenticated request and therefore one
// Cognito `GetUser` call. Cognito's quota — not Redis — is the binding
// constraint, which is why the rules below are conservative by default.

import {
    DEFAULT_HEARTBEAT_SECONDS,
    HEARTBEAT_JITTER_RATIO,
    HEARTBEAT_LEASE_KEY,
    LEASE_STALE_INTERVALS,
    MAX_HEARTBEAT_SECONDS,
    MIN_HEARTBEAT_SECONDS
} from "./constants";

/**
 * Clamp a configured interval into a sane range.
 *
 * The value is operator-supplied at runtime (`REACT_APP_PRESENCE_HEARTBEAT_SECONDS`)
 * so it can be widened without a rebuild if Cognito comes under pressure. The
 * floor stops a typo turning the fleet into a load generator; the ceiling stops
 * an interval so long that presence would expire between beats.
 *
 * @param {number|string|null|undefined} seconds
 * @returns {number} interval in milliseconds
 */
export const resolveIntervalMs = seconds => {
    const parsed = Number(seconds);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return DEFAULT_HEARTBEAT_SECONDS * 1000;
    }
    return Math.min(Math.max(parsed, MIN_HEARTBEAT_SECONDS), MAX_HEARTBEAT_SECONDS) * 1000;
};

/**
 * Apply +/- jitter so a fleet that reloaded together does not beat in lockstep.
 *
 * @param {number} intervalMs
 * @param {function(): number} [random] injectable for deterministic tests
 * @returns {number}
 */
export const jitteredDelayMs = (intervalMs, random = Math.random) => {
    const spread = intervalMs * HEARTBEAT_JITTER_RATIO;
    const offset = (random() * 2 - 1) * spread;
    return Math.max(Math.round(intervalMs + offset), 1000);
};

const parseLease = raw => {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.id === "string" && Number.isFinite(parsed.ts)) {
            return parsed;
        }
        return null;
    } catch (_) {
        // A corrupt lease is treated as no lease: someone will claim it and
        // overwrite the garbage on the next tick.
        return null;
    }
};

/**
 * Decide whether this tab currently owns the right to heartbeat.
 *
 * Ownership is advisory, not a lock: two tabs racing to claim an expired lease
 * may both send once. That is harmless — the server upsert is idempotent and
 * both beats describe the same session — so a real mutex would be complexity
 * bought for nothing.
 *
 * @returns {boolean}
 */
export const isLeaseHolder = ({ tabId, now, intervalMs, storage }) => {
    if (!tabId || !storage) {
        return false;
    }
    let lease = null;
    try {
        lease = parseLease(storage.getItem(HEARTBEAT_LEASE_KEY));
    } catch (_) {
        // Storage unavailable (private mode, quota, disabled). Fall back to
        // "this tab is the leader" so a single-tab user still reports presence.
        return true;
    }
    if (!lease) {
        return true;
    }
    if (lease.id === tabId) {
        return true;
    }
    return now - lease.ts > intervalMs * LEASE_STALE_INTERVALS;
};

/**
 * Record that this tab holds the lease. Failures are ignored: losing the lease
 * write only risks an extra heartbeat from a sibling tab.
 */
export const claimLease = ({ tabId, now, storage }) => {
    if (!tabId || !storage) {
        return;
    }
    try {
        storage.setItem(HEARTBEAT_LEASE_KEY, JSON.stringify({ id: tabId, ts: now }));
    } catch (_) {
        /* best effort */
    }
};

/**
 * Release the lease if this tab holds it, so a sibling can take over
 * immediately rather than waiting for it to go stale.
 */
export const releaseLease = ({ tabId, storage }) => {
    if (!tabId || !storage) {
        return;
    }
    try {
        const lease = parseLease(storage.getItem(HEARTBEAT_LEASE_KEY));
        if (lease && lease.id === tabId) {
            storage.removeItem(HEARTBEAT_LEASE_KEY);
        }
    } catch (_) {
        /* best effort */
    }
};

/**
 * The single decision point: should a heartbeat go out right now?
 *
 * Every condition is a hard gate, evaluated cheaply and in increasing order of
 * cost. Note in particular that a hidden tab never beats — a backgrounded tab
 * tells us nothing about whether the human is present, and browsers throttle
 * background timers anyway, so beating from one would be both misleading and
 * unreliable.
 *
 * @returns {{send: boolean, reason: string}} `reason` is returned rather than
 *   logged so callers (and tests) can assert on *why* a beat was skipped.
 */
export const evaluateHeartbeat = ({ flagEnabled, isSignedIn, visibilityState, tabId, now, intervalMs, storage }) => {
    if (!flagEnabled) {
        return { send: false, reason: "flag_off" };
    }
    if (!isSignedIn) {
        return { send: false, reason: "signed_out" };
    }
    if (visibilityState === "hidden") {
        return { send: false, reason: "tab_hidden" };
    }
    if (!isLeaseHolder({ tabId, now, intervalMs, storage })) {
        return { send: false, reason: "not_lease_holder" };
    }
    return { send: true, reason: "ok" };
};
